import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import { ensureRecentAdminReauth } from '@/lib/adminSecurity';
import { useAuth } from '@/context/AuthContext';
import { 
  Building2,
  Users,
  Database,
  Save,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Shield,
  CreditCard,
  ClipboardCheck,
  MapPin,
  Copy,
  Download,
  Smartphone,
  Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const ROLE_COLORS = {
  admin: 'bg-[#C97A5E]/10 text-[#C97A5E]',
  manager: 'bg-blue-100 text-blue-800',
  accountant: 'bg-green-100 text-green-800'
};

const buildBosUpiNote = (settings, bosRef = 'ASAC00001') => {
  const prefix = String(settings?.upi_note_prefix || '').trim();
  const billRef = String(bosRef || '').trim();
  if (prefix && billRef) return `${prefix} ${billRef}`.trim();
  return billRef;
};

const buildBosUpiUri = (settings, bosRef = 'ASAC00001') => {
  if (!settings?.upi_enabled) return '';
  const upiId = String(settings?.upi_id || '').trim();
  const payeeName = String(settings?.upi_payee_name || '').trim();
  if (!upiId || !payeeName) return '';
  const params = new URLSearchParams({
    pa: upiId,
    pn: payeeName,
  });
  const note = buildBosUpiNote(settings, bosRef);
  if (note) params.set('tn', note);
  return `upi://pay?${params.toString()}`;
};

export default function SettingsPage() {
  const { admin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('business');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seedingAccounts, setSeedingAccounts] = useState(false);
  const [businessInfoEditable, setBusinessInfoEditable] = useState(false);

  const [dbCollections, setDbCollections] = useState([]);
  const [dbSelectedCollections, setDbSelectedCollections] = useState([]);
  const [dbBackupLoading, setDbBackupLoading] = useState(false);
  const [dbBackupsLoading, setDbBackupsLoading] = useState(false);
  const [dbBackups, setDbBackups] = useState([]);
  const [dbRestoreLoading, setDbRestoreLoading] = useState(false);
  const [dbRestoreFilename, setDbRestoreFilename] = useState('');
  const [dbSelectedBackupKey, setDbSelectedBackupKey] = useState('');
  const [dbAutomationStatus, setDbAutomationStatus] = useState(null);
  const [dbAutomationLoading, setDbAutomationLoading] = useState(false);
  const [dbAutomationRunLoading, setDbAutomationRunLoading] = useState(false);
  const [dbResetLoading, setDbResetLoading] = useState(false);
  const [showDbResetDialog, setShowDbResetDialog] = useState(false);
  const [dbCounterCollections, setDbCounterCollections] = useState([]);
  const [dbSelectedCounters, setDbSelectedCounters] = useState([]);
  const [dbCounterResetLoading, setDbCounterResetLoading] = useState(false);
  const [showDbCounterResetDialog, setShowDbCounterResetDialog] = useState(false);
  const bosUpiQrRef = useRef(null);
  
  // Business Settings
  const [settings, setSettings] = useState({
    business_name: '',
    gst_number: '',
    gst_enabled: false,
    gst_percentage: 18,
    gst_invoice_prefix: 'GST',
    business_state: 'Kerala',
    business_state_code: '32',
    address: '',
    address_line1: '',
    address_line2: '',
    phone: '',
    email: '',
    fssai: '',
    payment_methods: [],
    upi_enabled: false,
    upi_id: '',
    upi_payee_name: '',
    upi_note_prefix: '',
    show_upi_text_on_bos: false,
  });
  const [mobileRelease, setMobileRelease] = useState({
    latest_version: '',
    latest_build_number: 0,
    minimum_supported_build_number: 0,
    force_update: false,
    is_active: false,
    message: '',
    apk_path: '',
    apk_url: '',
    released_at: '',
  });
  const [mobileReleaseSaving, setMobileReleaseSaving] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    types: ['payment_due', 'overdue'],
    frequency: 'daily',
    time: '09:30',
    minimum_outstanding: 0,
    dedupe_window_hours: 24,
  });
  const [whatsappSummarySettings, setWhatsappSummarySettings] = useState({
    enabled: false,
    time: '20:00',
    timezone: 'Asia/Kolkata',
    recipients: [],
    include_orders_total: true,
    include_collected_amount: true,
    include_outstanding_summary: true,
    include_gst_summary: true,
  });
  const [summaryRecipientInput, setSummaryRecipientInput] = useState('');
  const [savingAutomationSettings, setSavingAutomationSettings] = useState(false);
  const [mobileApkUploading, setMobileApkUploading] = useState(false);
  const [mobileApkUploadProgress, setMobileApkUploadProgress] = useState(0);
  const mobileApkInputRef = useRef(null);
  const [showMobileReleaseModal, setShowMobileReleaseModal] = useState(false);

  // Staff Types
  const [staffTypes, setStaffTypes] = useState([]);
  const [showStaffTypeModal, setShowStaffTypeModal] = useState(false);
  const [staffTypeForm, setStaffTypeForm] = useState({ name: '', is_active: true });
  const [editingStaffType, setEditingStaffType] = useState(null);
  const [staffTypeSaving, setStaffTypeSaving] = useState(false);

  // Return Reasons
  const [returnReasons, setReturnReasons] = useState([]);
  const [showReturnReasonModal, setShowReturnReasonModal] = useState(false);
  const [returnReasonForm, setReturnReasonForm] = useState({ name: '', is_active: true });
  const [editingReturnReason, setEditingReturnReason] = useState(null);
  const [returnReasonSaving, setReturnReasonSaving] = useState(false);

  // Area Masters
  const [areaMasters, setAreaMasters] = useState([]);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [areaForm, setAreaForm] = useState({ name: '', is_active: true });
  const [editingArea, setEditingArea] = useState(null);
  const [areaSaving, setAreaSaving] = useState(false);

  // Short URLs
  const [shortUrls, setShortUrls] = useState([]);
  const [shortUrlLoading, setShortUrlLoading] = useState(false);
  const [shortUrlSaving, setShortUrlSaving] = useState(false);
  const [showShortUrlModal, setShowShortUrlModal] = useState(false);
  const [creatingShortSamples, setCreatingShortSamples] = useState(false);
  const [shortUrlForm, setShortUrlForm] = useState({
    name: '',
    original_url: '',
    code: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    is_active: true
  });

  // QR Codes
  const [qrCodes, setQrCodes] = useState([]);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [qrCodeSaving, setQrCodeSaving] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);
  const [qrCodeForm, setQrCodeForm] = useState({
    name: '',
    original_url: '',
    code: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    is_active: true
  });
  
  // Testimonials
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [testimonialUpdatingId, setTestimonialUpdatingId] = useState('');

  // Admin Users
  const [adminUsers, setAdminUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [mfaStatus, setMfaStatus] = useState({ enabled: false, required: false });
  const [mfaSetupSecret, setMfaSetupSecret] = useState('');
  const [mfaSetupUrl, setMfaSetupUrl] = useState('');
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    name: '',
    role: 'manager'
  });

  const canManageSettings = hasPermission('all');
  const bosPreviewRef = 'ASAC00001';
  const bosUpiPreviewValue = buildBosUpiUri(settings, bosPreviewRef);
  const bosUpiPreviewNote = buildBosUpiNote(settings, bosPreviewRef);
  const backendBase = process.env.REACT_APP_BACKEND_URL || '';
  const shortUrlBase = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api/s/`
    : `${window.location.origin}/api/s/`;
  const getAssetUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/assets') && backendBase) return `${backendBase}${url}`;
    return url;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes, mobileReleaseRes] = await Promise.all([
        adminApi.getSettings(),
        canManageSettings ? adminApi.getAdminUsers() : Promise.resolve({ data: [] }),
        canManageSettings ? adminApi.getMobileReleaseSettings() : Promise.resolve({ data: null }),
      ]);
      
      setSettings(settingsRes.data);
      setBusinessInfoEditable(false);
      setAdminUsers(usersRes.data || []);
      if (canManageSettings) {
        const mfaRes = await adminApi.getAdminMfaStatus();
        setMfaStatus(mfaRes.data || { enabled: false, required: false });
      }
      if (mobileReleaseRes.data) {
        setMobileRelease({
          latest_version: mobileReleaseRes.data.latest_version || '',
          latest_build_number: mobileReleaseRes.data.latest_build_number || 0,
          minimum_supported_build_number: mobileReleaseRes.data.minimum_supported_build_number || 0,
          force_update: Boolean(mobileReleaseRes.data.force_update),
          is_active: Boolean(mobileReleaseRes.data.is_active),
          message: mobileReleaseRes.data.message || '',
          apk_path: mobileReleaseRes.data.apk_path || '',
          apk_url: mobileReleaseRes.data.apk_url || '',
          released_at: mobileReleaseRes.data.released_at || '',
        });
      }
      if (canManageSettings) {
        const staffTypesRes = await adminApi.getStaffTypes();
        setStaffTypes(staffTypesRes.data || []);
        const returnReasonsRes = await adminApi.getReturnReasons();
        setReturnReasons(returnReasonsRes.data || []);
        const areaMastersRes = await adminApi.getAreaMasters();
        setAreaMasters(areaMastersRes.data || []);
        const [reminderRes, whatsappSummaryRes] = await Promise.all([
          adminApi.getReminderSettings(),
          adminApi.getWhatsappSummarySettings(),
        ]);
        setReminderSettings((prev) => ({ ...prev, ...(reminderRes.data || {}) }));
        setWhatsappSummarySettings((prev) => ({ ...prev, ...(whatsappSummaryRes.data || {}) }));
        await fetchShortUrls();
        await fetchQrCodes();
        await fetchTestimonials();
        const collectionsRes = await adminApi.getDbCollections();
        setDbCollections(collectionsRes.data?.collections || []);
        setDbCounterCollections(collectionsRes.data?.counters || []);
        await Promise.all([fetchDbBackups(), fetchDbAutomationStatus()]);
      }
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const resetStaffTypeForm = () => {
    setStaffTypeForm({ name: '', is_active: true });
    setEditingStaffType(null);
  };

  const handleSaveStaffType = async () => {
    if (!staffTypeForm.name.trim()) {
      toast.error('Please enter staff type name');
      return;
    }

    setStaffTypeSaving(true);
    try {
      if (editingStaffType) {
        await adminApi.updateStaffType(editingStaffType.id, staffTypeForm);
        toast.success('Staff type updated');
      } else {
        await adminApi.createStaffType(staffTypeForm);
        toast.success('Staff type created');
      }
      const staffTypesRes = await adminApi.getStaffTypes();
      setStaffTypes(staffTypesRes.data || []);
      setShowStaffTypeModal(false);
      resetStaffTypeForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff type');
    } finally {
      setStaffTypeSaving(false);
    }
  };

  const handleEditStaffType = (type) => {
    setEditingStaffType(type);
    setStaffTypeForm({ name: type.name, is_active: type.is_active });
    setShowStaffTypeModal(true);
  };

  const handleDeleteStaffType = async (type) => {
    try {
      await adminApi.deleteStaffType(type.id);
      toast.success('Staff type deleted');
      const staffTypesRes = await adminApi.getStaffTypes();
      setStaffTypes(staffTypesRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff type');
    }
  };

  const resetReturnReasonForm = () => {
    setReturnReasonForm({ name: '', is_active: true });
    setEditingReturnReason(null);
  };

  const handleEditReturnReason = (reason) => {
    setEditingReturnReason(reason);
    setReturnReasonForm({ name: reason.name, is_active: reason.is_active });
    setShowReturnReasonModal(true);
  };

  const handleDeleteReturnReason = async (reason) => {
    try {
      await adminApi.deleteReturnReason(reason.id);
      toast.success('Return reason deleted');
      const returnReasonsRes = await adminApi.getReturnReasons();
      setReturnReasons(returnReasonsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete return reason');
    }
  };

  const handleSaveReturnReason = async () => {
    if (!returnReasonForm.name.trim()) {
      toast.error('Please enter a reason');
      return;
    }

    setReturnReasonSaving(true);
    try {
      if (editingReturnReason) {
        await adminApi.updateReturnReason(editingReturnReason.id, returnReasonForm);
        toast.success('Return reason updated');
      } else {
        await adminApi.createReturnReason(returnReasonForm);
        toast.success('Return reason created');
      }
      setShowReturnReasonModal(false);
      resetReturnReasonForm();
      const returnReasonsRes = await adminApi.getReturnReasons();
      setReturnReasons(returnReasonsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save return reason');
    } finally {
      setReturnReasonSaving(false);
    }
  };

  const resetAreaForm = () => {
    setAreaForm({ name: '', is_active: true });
    setEditingArea(null);
  };

  const handleEditArea = (area) => {
    setEditingArea(area);
    setAreaForm({ name: area.name, is_active: area.is_active });
    setShowAreaModal(true);
  };

  const handleDeleteArea = async (area) => {
    try {
      await adminApi.deleteAreaMaster(area.id);
      toast.success('Area deleted');
      const areaRes = await adminApi.getAreaMasters();
      setAreaMasters(areaRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete area');
    }
  };

  const handleSaveArea = async () => {
    if (!areaForm.name.trim()) {
      toast.error('Please enter area name');
      return;
    }

    setAreaSaving(true);
    try {
      if (editingArea) {
        await adminApi.updateAreaMaster(editingArea.id, areaForm);
        toast.success('Area updated');
      } else {
        await adminApi.createAreaMaster(areaForm);
        toast.success('Area created');
      }
      setShowAreaModal(false);
      resetAreaForm();
      const areaRes = await adminApi.getAreaMasters();
      setAreaMasters(areaRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save area');
    } finally {
      setAreaSaving(false);
    }
  };

  const resetShortUrlForm = () => {
    setShortUrlForm({
      name: '',
      original_url: '',
      code: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_content: '',
      utm_term: '',
      is_active: true
    });
  };

  const fetchShortUrls = async () => {
    setShortUrlLoading(true);
    try {
      const response = await adminApi.getShortUrls();
      setShortUrls(response.data?.items || []);
      if ((response.data?.items || []).length === 0 && !localStorage.getItem('short_url_samples_created')) {
        await createSampleShortUrls();
      }
    } catch (error) {
      toast.error('Failed to load short URLs');
    } finally {
      setShortUrlLoading(false);
    }
  };

  const createSampleShortUrls = async () => {
    setCreatingShortSamples(true);
    try {
      await adminApi.createShortUrl({
        name: 'Festival Offer',
        original_url: 'https://agniyaspices.com/offers',
        utm_source: 'whatsapp',
        utm_medium: 'message',
        utm_campaign: 'festival_offer',
        is_active: true
      });
      await adminApi.createShortUrl({
        name: 'New Product Launch',
        original_url: 'https://agniyaspices.com/new',
        utm_source: 'instagram',
        utm_medium: 'social',
        utm_campaign: 'new_launch',
        is_active: true
      });
      localStorage.setItem('short_url_samples_created', 'true');
      const response = await adminApi.getShortUrls();
      setShortUrls(response.data?.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sample links');
    } finally {
      setCreatingShortSamples(false);
    }
  };

  const handleSaveShortUrl = async () => {
    if (!shortUrlForm.original_url.trim()) {
      toast.error('Original URL is required');
      return;
    }
    setShortUrlSaving(true);
    try {
      await adminApi.createShortUrl({
        name: shortUrlForm.name || undefined,
        original_url: shortUrlForm.original_url,
        code: shortUrlForm.code || undefined,
        utm_source: shortUrlForm.utm_source || undefined,
        utm_medium: shortUrlForm.utm_medium || undefined,
        utm_campaign: shortUrlForm.utm_campaign || undefined,
        utm_content: shortUrlForm.utm_content || undefined,
        utm_term: shortUrlForm.utm_term || undefined,
        is_active: shortUrlForm.is_active
      });
      toast.success('Short URL created');
      setShowShortUrlModal(false);
      resetShortUrlForm();
      fetchShortUrls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create short URL');
    } finally {
      setShortUrlSaving(false);
    }
  };

  const handleCopyShortUrl = async (code) => {
    try {
      await navigator.clipboard.writeText(`${shortUrlBase}${code}`);
      toast.success('Short URL copied');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const resetQrCodeForm = () => {
    setQrCodeForm({
      name: '',
      original_url: '',
      code: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_content: '',
      utm_term: '',
      is_active: true
    });
  };

  const fetchQrCodes = async () => {
    setQrCodeLoading(true);
    try {
      const response = await adminApi.getQrCodes();
      setQrCodes(response.data?.items || []);
    } catch (error) {
      toast.error('Failed to load QR codes');
    } finally {
      setQrCodeLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    setTestimonialsLoading(true);
    try {
      const response = await adminApi.getTestimonials({ page_size: 200 });
      setTestimonials(response.data?.items || []);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const updateTestimonialFlag = async (testimonialId, payload) => {
    setTestimonialUpdatingId(testimonialId);
    try {
      await adminApi.updateTestimonial(testimonialId, payload);
      setTestimonials((prev) =>
        prev.map((item) => (item.id === testimonialId ? { ...item, ...payload } : item))
      );
      toast.success('Testimonial updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update testimonial');
    } finally {
      setTestimonialUpdatingId('');
    }
  };

  const handleSaveQrCode = async () => {
    if (!qrCodeForm.original_url.trim()) {
      toast.error('Original URL is required');
      return;
    }
    setQrCodeSaving(true);
    try {
      await adminApi.createQrCode({
        name: qrCodeForm.name || undefined,
        original_url: qrCodeForm.original_url,
        code: qrCodeForm.code || undefined,
        utm_source: qrCodeForm.utm_source || undefined,
        utm_medium: qrCodeForm.utm_medium || undefined,
        utm_campaign: qrCodeForm.utm_campaign || undefined,
        utm_content: qrCodeForm.utm_content || undefined,
        utm_term: qrCodeForm.utm_term || undefined,
        is_active: qrCodeForm.is_active
      });
      toast.success('QR code created');
      setShowQrCodeModal(false);
      resetQrCodeForm();
      fetchQrCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create QR code');
    } finally {
      setQrCodeSaving(false);
    }
  };

  const handleCopyQrLink = async (code, qrId) => {
    try {
      await navigator.clipboard.writeText(`${shortUrlBase}${code}?qr=${qrId}`);
      toast.success('QR link copied');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const handleDownloadQr = (qrId, name) => {
    const canvas = document.getElementById(`qr-canvas-${qrId}`);
    if (!canvas) {
      toast.error('QR code not ready');
      return;
    }
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${name || 'qr-code'}.png`;
    link.click();
  };

  const handleSaveSettings = async () => {
    if (!canManageSettings) {
      toast.error('You do not have permission to update settings');
      return;
    }
    
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      setBusinessInfoEditable(false);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAutomationSettings = async () => {
    if (!canManageSettings) {
      toast.error('You do not have permission to update automation settings');
      return;
    }
    setSavingAutomationSettings(true);
    try {
      await Promise.all([
        adminApi.updateReminderSettings(reminderSettings),
        adminApi.updateWhatsappSummarySettings(whatsappSummarySettings),
      ]);
      toast.success('Reminder and WhatsApp summary settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save automation settings');
    } finally {
      setSavingAutomationSettings(false);
    }
  };

  const addSummaryRecipient = () => {
    const phone = summaryRecipientInput.trim();
    if (!phone) return;
    setWhatsappSummarySettings((prev) => ({
      ...prev,
      recipients: Array.from(new Set([...(prev.recipients || []), phone])),
    }));
    setSummaryRecipientInput('');
  };

  const sendWhatsappSummaryNow = async () => {
    try {
      await adminApi.sendWhatsappDailySummary();
      toast.success('Daily WhatsApp summary sent/logged');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send daily summary');
    }
  };

  const handleSaveMobileRelease = async () => {
    if (!canManageSettings) {
      toast.error('You do not have permission to update mobile release settings');
      return false;
    }

    setMobileReleaseSaving(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return false;
      const payload = {
        ...mobileRelease,
        latest_build_number: Number(mobileRelease.latest_build_number || 0),
        minimum_supported_build_number: Number(mobileRelease.minimum_supported_build_number || 0),
        released_at: mobileRelease.released_at || undefined,
      };
      const response = await adminApi.updateMobileReleaseSettings(payload);
      setMobileRelease({
        latest_version: response.data.latest_version || '',
        latest_build_number: response.data.latest_build_number || 0,
        minimum_supported_build_number: response.data.minimum_supported_build_number || 0,
        force_update: Boolean(response.data.force_update),
        is_active: Boolean(response.data.is_active),
        message: response.data.message || '',
        apk_path: response.data.apk_path || '',
        apk_url: response.data.apk_url || '',
        released_at: response.data.released_at || '',
      });
      toast.success('Mobile release settings saved');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save mobile release settings');
      return false;
    } finally {
      setMobileReleaseSaving(false);
    }
  };

  const uploadApkToSignedStorageUrl = (uploadUrl, file) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.withCredentials = false;

      xhr.upload.onprogress = (event) => {
        const total = event.total || file.size || 0;
        if (total > 0) {
          setMobileApkUploadProgress(Math.round((event.loaded / total) * 100));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Direct upload to storage failed before the upload could complete.'));
      };

      xhr.onabort = () => {
        reject(new Error('Direct upload to storage was cancelled.'));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        const responseText = String(xhr.responseText || '').trim();
        const message = responseText || xhr.statusText || 'Storage upload failed';
        reject(new Error(`Storage upload failed (${xhr.status || 0}): ${message}`));
      };

      xhr.send(file);
    });

  const handleUploadMobileApk = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.apk')) {
      toast.error('Please choose a valid APK file');
      return;
    }

    setMobileApkUploading(true);
    setMobileApkUploadProgress(0);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const prepareResponse = await adminApi.prepareMobileReleaseApkUpload({
        file_name: file.name,
        content_type: file.type || 'application/vnd.android.package-archive',
        size_bytes: file.size || 0,
        latest_version: mobileRelease.latest_version,
        latest_build_number: mobileRelease.latest_build_number,
      });

      const uploadUrl = prepareResponse.data?.upload_url;
      if (!uploadUrl) {
        throw new Error('Upload URL was not generated');
      }

      await uploadApkToSignedStorageUrl(uploadUrl, file);

      setMobileRelease((prev) => ({
        ...prev,
        apk_path: prepareResponse.data?.apk_path || '',
        apk_url: prepareResponse.data?.apk_url || '',
      }));
      toast.success('APK uploaded successfully');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' && detail) ||
        (typeof detail?.message === 'string' && detail.message) ||
        error?.message ||
        'Failed to upload APK'
      );
    } finally {
      setMobileApkUploading(false);
      setMobileApkUploadProgress(0);
    }
  };

  const handleSeedAccounts = async () => {
    if (!canManageSettings) {
      toast.error('You do not have permission to seed accounts');
      return;
    }

    setSeedingAccounts(true);
    try {
      await adminApi.seedAccounts();
      toast.success('Default accounts seeded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed accounts');
    } finally {
      setSeedingAccounts(false);
    }
  };

  const toggleDbCollection = (name, checked) => {
    setDbSelectedCollections((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, name]));
      }
      return prev.filter((c) => c !== name);
    });
  };

  const fetchDbBackups = async () => {
    setDbBackupsLoading(true);
    try {
      const res = await adminApi.getDbBackups({ limit: 100 });
      const items = res.data?.items || [];
      setDbBackups(items);
      if (!dbSelectedBackupKey && items.length) {
        setDbSelectedBackupKey(items[0].backup_key || '');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load backups');
    } finally {
      setDbBackupsLoading(false);
    }
  };

  const fetchDbAutomationStatus = async () => {
    setDbAutomationLoading(true);
    try {
      const res = await adminApi.getDbAutomationStatus();
      setDbAutomationStatus(res.data || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load backup automation status');
    } finally {
      setDbAutomationLoading(false);
    }
  };

  const handleDbBackup = async () => {
    setDbBackupLoading(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const res = await adminApi.backupDatabase();
      const filename = res.data?.filename || 'backup.json';
      toast.success(`Backup created: ${filename}`);
      await Promise.all([fetchDbBackups(), fetchDbAutomationStatus()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setDbBackupLoading(false);
    }
  };

  const handleDbRestore = async () => {
    setDbRestoreLoading(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const payload = {
        target: 'sandbox',
        clear_existing: true,
      };
      if ((dbSelectedBackupKey || '').trim()) {
        payload.backup_key = dbSelectedBackupKey.trim();
      }
      if ((dbRestoreFilename || '').trim()) {
        payload.filename = dbRestoreFilename.trim();
      }

      const res = await adminApi.restoreBackup(payload);
      const filename = res.data?.filename || dbRestoreFilename || dbSelectedBackupKey || 'latest backup';
      const restoredCount = Number(res.data?.collection_count || 0);
      toast.success(`Restored ${restoredCount} collections from ${filename} to sandbox`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup to sandbox');
    } finally {
      setDbRestoreLoading(false);
    }
  };

  const handleRunDbAutomation = async () => {
    setDbAutomationRunLoading(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      await adminApi.runDbAutomation();
      toast.success('Backup automation run completed');
      await Promise.all([fetchDbBackups(), fetchDbAutomationStatus()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run backup automation');
    } finally {
      setDbAutomationRunLoading(false);
    }
  };

  const handleDbReset = async () => {
    if (!dbSelectedCollections.length) {
      toast.error('Select at least one collection');
      return;
    }
    setShowDbResetDialog(true);
  };

  const toggleDbCounter = (name, checked) => {
    setDbSelectedCounters((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, name]));
      }
      return prev.filter((c) => c !== name);
    });
  };

  const handleCounterReset = async () => {
    if (!dbSelectedCounters.length) {
      toast.error('Select at least one counter');
      return;
    }
    setShowDbCounterResetDialog(true);
  };

  const confirmDbReset = async () => {
    setDbResetLoading(true);
    try {
      const res = await adminApi.resetDatabase({ collections: dbSelectedCollections });
      const cleared = res.data?.cleared || {};
      const count = Object.keys(cleared).length;
      toast.success(`Cleared ${count} collection${count !== 1 ? 's' : ''}`);
      setShowDbResetDialog(false);
      setDbSelectedCollections([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset collections');
    } finally {
      setDbResetLoading(false);
    }
  };

  const confirmCounterReset = async () => {
    setDbCounterResetLoading(true);
    try {
      const res = await adminApi.resetCounters({ collections: dbSelectedCounters });
      const updated = res.data?.updated || {};
      const count = Object.keys(updated).length;
      toast.success(`Reset ${count} counter${count !== 1 ? 's' : ''}`);
      setShowDbCounterResetDialog(false);
      setDbSelectedCounters([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset counters');
    } finally {
      setDbCounterResetLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!userForm.username || !userForm.password || !userForm.name) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await adminApi.createAdminUser(userForm);
      toast.success('Admin user created successfully');
      setShowUserModal(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: userForm.name,
        role: userForm.role
      };
      if (userForm.password) {
        updateData.password = userForm.password;
      }
      
      await adminApi.updateAdminUser(selectedUser.id, updateData);
      toast.success('Admin user updated successfully');
      setShowUserModal(false);
      setIsEditing(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await adminApi.deleteAdminUser(selectedUser.id);
      toast.success('Admin user deleted');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      username: user.username,
      password: '',
      name: user.name,
      role: user.role
    });
    setIsEditing(true);
    setShowUserModal(true);
  };

  const resetUserForm = () => {
    setUserForm({
      username: '',
      password: '',
      name: '',
      role: 'manager'
    });
    setSelectedUser(null);
    setIsEditing(false);
  };

  const handleSetupMfa = async () => {
    setMfaBusy(true);
    try {
      const res = await adminApi.setupAdminMfa();
      setMfaSetupSecret(res.data?.secret || '');
      setMfaSetupUrl(res.data?.otpauth_url || '');
      toast.success('MFA setup secret generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setMfaBusy(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaVerifyCode.trim()) {
      toast.error('Enter the authenticator code');
      return;
    }
    setMfaBusy(true);
    try {
      await adminApi.verifyAdminMfa({ code: mfaVerifyCode });
      setMfaStatus({ ...mfaStatus, enabled: true });
      setMfaSetupSecret('');
      setMfaSetupUrl('');
      setMfaVerifyCode('');
      toast.success('MFA enabled successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify MFA');
    } finally {
      setMfaBusy(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!mfaDisablePassword.trim() || !mfaDisableCode.trim()) {
      toast.error('Enter password and MFA code');
      return;
    }
    setMfaBusy(true);
    try {
      await adminApi.disableAdminMfa({ password: mfaDisablePassword, code: mfaDisableCode });
      setMfaStatus({ ...mfaStatus, enabled: false });
      setMfaDisablePassword('');
      setMfaDisableCode('');
      toast.success('MFA disabled successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setMfaBusy(false);
    }
  };

  const togglePaymentMethod = (method) => {
    const methods = settings.payment_methods || [];
    if (methods.includes(method)) {
      setSettings({ ...settings, payment_methods: methods.filter(m => m !== method) });
    } else {
      setSettings({ ...settings, payment_methods: [...methods, method] });
    }
  };

  const handleDownloadBosUpiQr = () => {
    const qrCanvas = bosUpiQrRef.current?.querySelector('canvas');
    const upiId = String(settings.upi_id || '').trim() || 'upi';
    if (!qrCanvas) {
      toast.error('QR preview is not ready to download yet');
      return;
    }
    const link = document.createElement('a');
    link.href = qrCanvas.toDataURL('image/png');
    link.download = `${upiId.replace(/[^a-zA-Z0-9._-]+/g, '-')}-payment-qr.png`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Settings</h1>
        <p className="text-[#A8A8A8] mt-1">Configure your admin panel and business settings</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#FAFAF9] border border-[#E5E5E5]">
          <TabsTrigger value="business" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
            <Building2 className="w-4 h-4 mr-2" /> Business Profile
          </TabsTrigger>
          {canManageSettings && (
            <TabsTrigger value="staff-types" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Staff Types
            </TabsTrigger>
          )}
          {canManageSettings && (
            <TabsTrigger value="return-reasons" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
              <ClipboardCheck className="w-4 h-4 mr-2" /> Return Reasons
            </TabsTrigger>
          )}
          {canManageSettings && (
            <TabsTrigger value="areas" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-2" /> Areas
            </TabsTrigger>
          )}
          {canManageSettings && (
            <TabsTrigger value="users" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Admin Users
            </TabsTrigger>
          )}
          {canManageSettings && (
            <TabsTrigger value="database" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
              <Database className="w-4 h-4 mr-2" /> Database Administration
            </TabsTrigger>
          )}
          {canManageSettings && (
            <TabsTrigger value="mobile-release" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
              <Smartphone className="w-4 h-4 mr-2" /> Mobile Release
            </TabsTrigger>
          )}
          <TabsTrigger value="payments" className="data-[state=active]:bg-[#C97A5E] data-[state=active]:text-white">
            <CreditCard className="w-4 h-4 mr-2" /> Payment Methods
          </TabsTrigger>
        </TabsList>

        {/* Business Profile Tab */}
        <TabsContent value="business" className="space-y-6 mt-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg text-[#2D2420]">Business Information</CardTitle>
              <CardDescription>Basic information about your business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">Business Name</Label>
	                  <Input
	                    value={settings.business_name}
	                    onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
	                    placeholder="Enter business name"
	                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
	                    disabled={!canManageSettings || !businessInfoEditable}
	                    data-testid="business-name-input"
	                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">Phone</Label>
	                  <Input
	                    value={settings.phone}
	                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
	                    placeholder="Enter phone number"
	                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
	                    disabled={!canManageSettings || !businessInfoEditable}
	                    data-testid="business-phone-input"
	                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">Email</Label>
	                  <Input
	                    type="email"
	                    value={settings.email}
	                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
	                    placeholder="Enter email"
	                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
	                    disabled={!canManageSettings || !businessInfoEditable}
	                    data-testid="business-email-input"
	                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Address Line 1</Label>
	                <Input
	                  value={settings.address_line1}
	                  onChange={(e) => setSettings({ ...settings, address_line1: e.target.value })}
	                  placeholder="Enter address line 1"
	                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
	                  disabled={!canManageSettings || !businessInfoEditable}
	                  data-testid="business-address-line1-input"
	                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Address Line 2</Label>
	                <Input
	                  value={settings.address_line2}
	                  onChange={(e) => setSettings({ ...settings, address_line2: e.target.value })}
	                  placeholder="Enter address line 2"
	                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
	                  disabled={!canManageSettings || !businessInfoEditable}
	                  data-testid="business-address-line2-input"
	                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">FSSAI</Label>
	                <Input
	                  value={settings.fssai}
	                  onChange={(e) => setSettings({ ...settings, fssai: e.target.value })}
	                  placeholder="Enter FSSAI"
	                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
	                  disabled={!canManageSettings || !businessInfoEditable}
	                  data-testid="business-fssai-input"
	                />
	              </div>
	              {canManageSettings && (
	                <div className="flex justify-end">
	                  <Button
	                    type="button"
	                    variant={businessInfoEditable ? 'outline' : 'default'}
	                    onClick={() => setBusinessInfoEditable((prev) => !prev)}
	                    className={businessInfoEditable ? 'border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10' : 'bg-[#C97A5E] hover:bg-[#B06850]'}
	                    data-testid="edit-business-details-btn"
	                  >
	                    <Edit className="w-4 h-4 mr-2" />
	                    {businessInfoEditable ? 'Stop Editing' : 'Edit Details'}
	                  </Button>
	                </div>
	              )}
	            </CardContent>
	          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg text-[#2D2420]">GST Configuration</CardTitle>
              <CardDescription>Configure GST settings for invoicing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#5C4033]">Enable GST</Label>
                  <p className="text-sm text-[#A8A8A8]">Add GST to all invoices</p>
                </div>
                <Switch
                  checked={settings.gst_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, gst_enabled: checked })}
                  disabled={!canManageSettings}
                  data-testid="gst-toggle"
                />
              </div>
              
              {settings.gst_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[#E5E5E5]">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">GST Number</Label>
                    <Input
                      value={settings.gst_number}
                      onChange={(e) => setSettings({ ...settings, gst_number: e.target.value })}
                      placeholder="Enter GSTIN"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      disabled={!canManageSettings}
                      data-testid="gst-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">GST Percentage (%)</Label>
                    <Input
                      type="number"
                      value={settings.gst_percentage}
                      onChange={(e) => setSettings({ ...settings, gst_percentage: parseFloat(e.target.value) })}
                      placeholder="18"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      disabled={!canManageSettings}
                      data-testid="gst-percentage-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">GST Invoice Prefix</Label>
                    <Input
                      value={settings.gst_invoice_prefix || ''}
                      onChange={(e) => setSettings({ ...settings, gst_invoice_prefix: e.target.value })}
                      placeholder="GST"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      disabled={!canManageSettings}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Business State</Label>
                    <Input
                      value={settings.business_state || ''}
                      onChange={(e) => setSettings({ ...settings, business_state: e.target.value })}
                      placeholder="Kerala"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      disabled={!canManageSettings}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Business State Code</Label>
                    <Input
                      value={settings.business_state_code || ''}
                      onChange={(e) => setSettings({ ...settings, business_state_code: e.target.value })}
                      placeholder="32"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      disabled={!canManageSettings}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {canManageSettings && (
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Reminder & WhatsApp Summary Automation</CardTitle>
                <CardDescription>Configure staff payment reminders and daily business summaries.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4 rounded-xl border border-[#E5E5E5] p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-[#5C4033]">Payment Reminder Automation</Label>
                        <p className="text-sm text-[#A8A8A8]">Push/in-app reminders go only to assigned staff.</p>
                      </div>
                      <Switch
                        checked={Boolean(reminderSettings.enabled)}
                        onCheckedChange={(checked) => setReminderSettings((prev) => ({ ...prev, enabled: checked }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Reminder Time</Label>
                        <Input type="time" value={reminderSettings.time || '09:30'} onChange={(e) => setReminderSettings((prev) => ({ ...prev, time: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Min Outstanding</Label>
                        <Input type="number" value={reminderSettings.minimum_outstanding || 0} onChange={(e) => setReminderSettings((prev) => ({ ...prev, minimum_outstanding: Number(e.target.value || 0) }))} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 rounded-xl border border-[#E5E5E5] p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-[#5C4033]">Daily WhatsApp Summary</Label>
                        <p className="text-sm text-[#A8A8A8]">Orders, collections, outstanding, and GST summary to configured contacts.</p>
                      </div>
                      <Switch
                        checked={Boolean(whatsappSummarySettings.enabled)}
                        onCheckedChange={(checked) => setWhatsappSummarySettings((prev) => ({ ...prev, enabled: checked }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Summary Time</Label>
                        <Input type="time" value={whatsappSummarySettings.time || '20:00'} onChange={(e) => setWhatsappSummarySettings((prev) => ({ ...prev, time: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Add Recipient</Label>
                        <div className="flex gap-2">
                          <Input value={summaryRecipientInput} onChange={(e) => setSummaryRecipientInput(e.target.value)} placeholder="+91..." />
                          <Button type="button" variant="outline" onClick={addSummaryRecipient}>Add</Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(whatsappSummarySettings.recipients || []).map((phone) => (
                        <Badge key={phone} variant="outline" className="gap-2">
                          {phone}
                          <button type="button" onClick={() => setWhatsappSummarySettings((prev) => ({ ...prev, recipients: (prev.recipients || []).filter((item) => item !== phone) }))}>×</button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={sendWhatsappSummaryNow}>Send Summary Now</Button>
                  <Button onClick={handleSaveAutomationSettings} disabled={savingAutomationSettings}>
                    {savingAutomationSettings ? 'Saving...' : 'Save Automation Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {canManageSettings && (
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Push Notifications</CardTitle>
                <CardDescription>Create targeted push campaigns for staff, admins, and future customer audiences.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-[#5C4033] space-y-1">
                  <p>Use the dedicated Push Notifications module for:</p>
                  <ul className="list-disc pl-5 text-[#A8A8A8]">
                    <li>specific staff and customer-linked targeting</li>
                    <li>structured payloads and deep links</li>
                    <li>drafts, delivery history, and campaign review</li>
                  </ul>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => navigate('/push-notifications')}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    data-testid="open-push-module-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Open Push Module
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {canManageSettings && (
            <div className="flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleSeedAccounts}
                disabled={seedingAccounts}
                className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                data-testid="seed-accounts-btn"
              >
                {seedingAccounts ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Seed Default Accounts (Test)
              </Button>
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
                data-testid="save-settings-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </div>
          )}
        </TabsContent>

        {canManageSettings && (
          <TabsContent value="mobile-release" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">Internal Mobile Release</CardTitle>
                  <CardDescription>
                    Track the current Android release and open the release form only when you want to create or edit it.
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowMobileReleaseModal(true)}
                  className="bg-[#C97A5E] hover:bg-[#B06850] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {mobileRelease.latest_version ? 'Edit Release' : 'Create Release'}
                </Button>
              </CardHeader>
              <CardContent>
                {mobileRelease.latest_version ? (
                  <div className="overflow-x-auto rounded-xl border border-[#E5E5E5]">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#FAFAF9]">
                        <tr className="text-left text-[#5C4033]">
                          <th className="px-4 py-3 font-semibold">Version</th>
                          <th className="px-4 py-3 font-semibold">Build</th>
                          <th className="px-4 py-3 font-semibold">Min Build</th>
                          <th className="px-4 py-3 font-semibold">Status</th>
                          <th className="px-4 py-3 font-semibold">Force Update</th>
                          <th className="px-4 py-3 font-semibold">APK</th>
                          <th className="px-4 py-3 font-semibold">Released At</th>
                          <th className="px-4 py-3 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-[#E5E5E5] text-[#2D2420]">
                          <td className="px-4 py-3 font-medium">{mobileRelease.latest_version}</td>
                          <td className="px-4 py-3">{mobileRelease.latest_build_number}</td>
                          <td className="px-4 py-3">{mobileRelease.minimum_supported_build_number}</td>
                          <td className="px-4 py-3">
                            <Badge className={mobileRelease.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>
                              {mobileRelease.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={mobileRelease.force_update ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                              {mobileRelease.force_update ? 'Required' : 'Optional'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {mobileRelease.apk_path ? (
                              <span className="text-xs text-[#8B6F62]">Private GCS object</span>
                            ) : (
                              <span className="text-xs text-[#A8A8A8]">Missing</span>
                            )}
                          </td>
                          <td className="px-4 py-3">{mobileRelease.released_at || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              {mobileRelease.apk_url ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(mobileRelease.apk_url, '_blank', 'noopener,noreferrer')}
                                  className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Test Link
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => setShowMobileReleaseModal(true)}
                                className="bg-[#C97A5E] hover:bg-[#B06850]"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-[#E5E5E5] bg-[#FAFAF9] px-6 py-10 text-center">
                    <Smartphone className="mx-auto mb-3 h-8 w-8 text-[#C97A5E]" />
                    <p className="text-base font-medium text-[#2D2420]">No mobile release created yet</p>
                    <p className="mt-1 text-sm text-[#A8A8A8]">Create the first internal Android release when your signed APK is ready.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={showMobileReleaseModal} onOpenChange={setShowMobileReleaseModal}>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-[#2D2420]">
                    {mobileRelease.latest_version ? 'Edit Mobile Release' : 'Create Mobile Release'}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">Latest Version</Label>
                      <Input
                        value={mobileRelease.latest_version}
                        onChange={(e) => setMobileRelease((prev) => ({ ...prev, latest_version: e.target.value }))}
                        placeholder="1.0.3"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">Latest Build Number</Label>
                      <Input
                        type="number"
                        min="1"
                        value={mobileRelease.latest_build_number}
                        onChange={(e) => setMobileRelease((prev) => ({ ...prev, latest_build_number: e.target.value }))}
                        placeholder="103"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">Minimum Supported Build</Label>
                      <Input
                        type="number"
                        min="1"
                        value={mobileRelease.minimum_supported_build_number}
                        onChange={(e) => setMobileRelease((prev) => ({ ...prev, minimum_supported_build_number: e.target.value }))}
                        placeholder="100"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">Released At</Label>
                      <Input
                        value={mobileRelease.released_at}
                        onChange={(e) => setMobileRelease((prev) => ({ ...prev, released_at: e.target.value }))}
                        placeholder="2026-03-26T10:30:00+05:30"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-[#E5E5E5] p-4">
                      <div className="space-y-1">
                        <Label className="text-[#5C4033]">Private APK Upload</Label>
                        <p className="text-sm text-[#A8A8A8]">
                          Upload the signed APK to private GCS. The app will receive only a generated signed download URL.
                        </p>
                        <p className="text-xs text-[#8B6F62]">
                          {mobileRelease.apk_path ? `Stored object: ${mobileRelease.apk_path}` : 'No APK uploaded yet'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {mobileRelease.apk_url ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => window.open(mobileRelease.apk_url, '_blank', 'noopener,noreferrer')}
                            className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Test Signed Link
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          onClick={() => mobileApkInputRef.current?.click()}
                          disabled={mobileApkUploading}
                          className="bg-[#C97A5E] hover:bg-[#B06850]"
                        >
                          {mobileApkUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          {mobileApkUploading ? 'Uploading...' : 'Upload APK'}
                        </Button>
                        <input
                          ref={mobileApkInputRef}
                          type="file"
                          accept=".apk,application/vnd.android.package-archive"
                          className="hidden"
                          onChange={handleUploadMobileApk}
                        />
                      </div>
                    </div>

                    {mobileApkUploading ? (
                      <div className="space-y-2">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-[#EDE7E3]">
                          <div
                            className="h-full rounded-full bg-[#C97A5E] transition-all"
                            style={{ width: `${mobileApkUploadProgress}%` }}
                          />
                        </div>
                        <p className="text-sm text-[#8B6F62]">{mobileApkUploadProgress}% uploaded</p>
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Release Message</Label>
                    <Textarea
                      value={mobileRelease.message}
                      onChange={(e) => setMobileRelease((prev) => ({ ...prev, message: e.target.value }))}
                      placeholder="What changed in this release, plus any install guidance for staff."
                      className="border-[#E5E5E5] focus:border-[#C97A5E] min-h-[120px]"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between rounded-xl border border-[#E5E5E5] p-4">
                      <div>
                        <p className="font-medium text-[#2D2420]">Release Active</p>
                        <p className="text-sm text-[#A8A8A8]">If disabled, the app will not show update prompts.</p>
                      </div>
                      <Switch
                        checked={mobileRelease.is_active}
                        onCheckedChange={(checked) => setMobileRelease((prev) => ({ ...prev, is_active: checked }))}
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-[#E5E5E5] p-4">
                      <div>
                        <p className="font-medium text-[#2D2420]">Force Update</p>
                        <p className="text-sm text-[#A8A8A8]">Block app usage until users install the required build.</p>
                      </div>
                      <Switch
                        checked={mobileRelease.force_update}
                        onCheckedChange={(checked) => setMobileRelease((prev) => ({ ...prev, force_update: checked }))}
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowMobileReleaseModal(false)}
                    className="border-[#E5E5E5]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      const saved = await handleSaveMobileRelease();
                      if (saved) {
                        setShowMobileReleaseModal(false);
                      }
                    }}
                    disabled={mobileReleaseSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {mobileReleaseSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Release
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Database Administration Tab */}
        {canManageSettings && (
          <TabsContent value="database" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Database Backup</CardTitle>
                <CardDescription>Create a full JSON backup in GCS.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-[#5C4033]">
                  {dbBackupsLoading ? 'Loading backups...' : `Available backups: ${dbBackups.length}`}
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleDbBackup}
                    disabled={dbBackupLoading}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    data-testid="db-backup-btn"
                  >
                    {dbBackupLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Create Backup
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Backup Automation (IST)</CardTitle>
                <CardDescription>
                  Daily backup + Monday archive of last 7 days + calendar 2-month retention.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#5C4033]">
                  <div>Timezone: {dbAutomationStatus?.timezone || 'Asia/Kolkata'}</div>
                  <div>Bucket: {dbAutomationStatus?.storage?.bucket || '-'}</div>
                  <div>Daily backups: {dbAutomationStatus?.counts?.backups ?? '-'}</div>
                  <div>Archives: {dbAutomationStatus?.counts?.archives ?? '-'}</div>
                </div>
                <div className="text-xs text-[#7A6A60]">
                  Latest backup: {dbAutomationStatus?.latest_backup?.filename || '-'}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={fetchDbAutomationStatus}
                    disabled={dbAutomationLoading}
                    className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                  >
                    {dbAutomationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Refresh Status
                  </Button>
                  <Button
                    onClick={handleRunDbAutomation}
                    disabled={dbAutomationRunLoading}
                    className="bg-[#5C4033] hover:bg-[#4A3329]"
                    data-testid="db-automation-run-btn"
                  >
                    {dbAutomationRunLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                    Run Automation Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Restore Backup to Sandbox</CardTitle>
                <CardDescription>
                  Restore a GCS backup into sandbox database agniya_spices_sandbox. Select a backup key or leave blank for latest.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#2D2420]">Select Backup</Label>
                  <Select value={dbSelectedBackupKey} onValueChange={setDbSelectedBackupKey}>
                    <SelectTrigger className="mt-2 border-[#E5E5E5]" data-testid="db-restore-backup-key">
                      <SelectValue placeholder={dbBackupsLoading ? 'Loading backups...' : 'Select backup key'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(dbBackups || []).map((item) => (
                        <SelectItem key={item.backup_key} value={item.backup_key}>
                          {item.filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#2D2420]">Backup Filename (legacy fallback, optional)</Label>
                  <Input
                    value={dbRestoreFilename}
                    onChange={(e) => setDbRestoreFilename(e.target.value)}
                    placeholder="backup_YYYYMMDD_HHMMSS.json"
                    className="mt-2 border-[#E5E5E5]"
                    data-testid="db-restore-filename"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleDbRestore}
                    disabled={dbRestoreLoading}
                    className="bg-[#5C4033] hover:bg-[#4A3329]"
                    data-testid="db-restore-btn"
                  >
                    {dbRestoreLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                    Restore to Sandbox
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Database Data Reset</CardTitle>
                <CardDescription>Select collections to clear. Critical system collections are protected. This cannot be undone.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDbSelectedCollections(dbCollections)}
                    className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                    data-testid="db-select-all"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDbSelectedCollections([])}
                    className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                    data-testid="db-clear-selection"
                  >
                    Clear Selection
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dbCollections.map((name) => (
                    <label key={name} className="flex items-center gap-2 text-[#2D2420]">
                      <Checkbox
                        checked={dbSelectedCollections.includes(name)}
                        onCheckedChange={(checked) => toggleDbCollection(name, checked === true)}
                        data-testid={`db-collection-${name}`}
                      />
                      <span className="text-sm">{name}</span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleDbReset}
                    disabled={dbResetLoading || !dbSelectedCollections.length}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="db-reset-btn"
                  >
                    {dbResetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Clear Selected Collections
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Reset Sequence Counters</CardTitle>
                <CardDescription>Reset selected counter collections so the next generated number starts from 1 (without clearing documents).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDbSelectedCounters(dbCounterCollections)}
                    className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                    data-testid="db-counter-select-all"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setDbSelectedCounters([])}
                    className="border-[#C97A5E] text-[#5C4033] hover:bg-[#C97A5E]/10"
                    data-testid="db-counter-clear-selection"
                  >
                    Clear Selection
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dbCounterCollections.length ? (
                    dbCounterCollections.map((name) => (
                      <label key={name} className="flex items-center gap-2 text-[#2D2420]">
                        <Checkbox
                          checked={dbSelectedCounters.includes(name)}
                          onCheckedChange={(checked) => toggleDbCounter(name, checked === true)}
                          data-testid={`db-counter-${name}`}
                        />
                        <span className="text-sm">{name}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-[#A8A8A8]">No counter collections found.</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleCounterReset}
                    disabled={dbCounterResetLoading || !dbSelectedCounters.length}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    data-testid="db-counter-reset-btn"
                  >
                    {dbCounterResetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Reset Selected Counters
                  </Button>
                </div>
              </CardContent>
            </Card>

            <AlertDialog open={showDbResetDialog} onOpenChange={setShowDbResetDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm data reset</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete data from the selected collections. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={dbResetLoading}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmDbReset}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={dbResetLoading}
                  >
                    {dbResetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Yes, clear data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDbCounterResetDialog} onOpenChange={setShowDbCounterResetDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm counter reset</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset sequence values so new numbers start again from 1 for selected counters. Existing documents will remain.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={dbCounterResetLoading}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmCounterReset}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    disabled={dbCounterResetLoading}
                  >
                    {dbCounterResetLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Yes, reset counters
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>
        )}

        {/* Admin Users Tab */}
        {canManageSettings && (
          <TabsContent value="staff-types" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">Staff Types</CardTitle>
                  <CardDescription>Manage staff type master data</CardDescription>
                </div>
                <Button
                  onClick={() => { resetStaffTypeForm(); setShowStaffTypeModal(true); }}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                  data-testid="add-staff-type-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Type
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {staffTypes.map((type) => (
                        <tr key={type.id} className="hover:bg-[#FAFAF9]/50" data-testid={`staff-type-row-${type.id}`}>
                          <td className="py-3 px-4 text-[#2D2420] font-medium">{type.name}</td>
                          <td className="py-3 px-4">
                            <Badge className={type.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {type.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditStaffType(type)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                data-testid={`edit-staff-type-${type.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteStaffType(type)}
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-staff-type-${type.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Dialog open={showStaffTypeModal} onOpenChange={setShowStaffTypeModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#2D2420]">{editingStaffType ? 'Edit Staff Type' : 'Add Staff Type'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Name</Label>
                    <Input
                      value={staffTypeForm.name}
                      onChange={(e) => setStaffTypeForm({ ...staffTypeForm, name: e.target.value })}
                      placeholder="Enter staff type name"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      data-testid="staff-type-name-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-[#5C4033]">Active</Label>
                      <p className="text-sm text-[#A8A8A8]">Enable this staff type</p>
                    </div>
                    <Switch
                      checked={staffTypeForm.is_active}
                      onCheckedChange={(checked) => setStaffTypeForm({ ...staffTypeForm, is_active: checked })}
                      data-testid="staff-type-active"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveStaffType}
                    disabled={staffTypeSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    data-testid="save-staff-type-btn"
                  >
                    {staffTypeSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Return Reasons Tab */}
        {canManageSettings && (
          <TabsContent value="return-reasons" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">Return Reasons</CardTitle>
                  <CardDescription>Predefined reasons for sales returns</CardDescription>
                </div>
                <Button
                  onClick={() => { resetReturnReasonForm(); setShowReturnReasonModal(true); }}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                  data-testid="add-return-reason-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Reason
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {returnReasons.map((reason) => (
                        <tr key={reason.id} className="hover:bg-[#FAFAF9]/50" data-testid={`return-reason-row-${reason.id}`}>
                          <td className="py-3 px-4 text-[#2D2420] font-medium">{reason.name}</td>
                          <td className="py-3 px-4">
                            <Badge className={reason.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {reason.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditReturnReason(reason)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                data-testid={`edit-return-reason-${reason.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteReturnReason(reason)}
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-return-reason-${reason.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Dialog open={showReturnReasonModal} onOpenChange={setShowReturnReasonModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#2D2420]">{editingReturnReason ? 'Edit Return Reason' : 'Add Return Reason'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Name</Label>
                    <Input
                      value={returnReasonForm.name}
                      onChange={(e) => setReturnReasonForm({ ...returnReasonForm, name: e.target.value })}
                      placeholder="Enter return reason"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      data-testid="return-reason-name-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-[#5C4033]">Active</Label>
                      <p className="text-sm text-[#A8A8A8]">Enable this reason</p>
                    </div>
                    <Switch
                      checked={returnReasonForm.is_active}
                      onCheckedChange={(checked) => setReturnReasonForm({ ...returnReasonForm, is_active: checked })}
                      data-testid="return-reason-active"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveReturnReason}
                    disabled={returnReasonSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    data-testid="save-return-reason-btn"
                  >
                    {returnReasonSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {canManageSettings && (
          <TabsContent value="areas" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">Areas</CardTitle>
                  <CardDescription>Manage customer and staff area master data</CardDescription>
                </div>
                <Button
                  onClick={() => { resetAreaForm(); setShowAreaModal(true); }}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                  data-testid="add-area-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Area
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {areaMasters.map((area) => (
                        <tr key={area.id} className="hover:bg-[#FAFAF9]/50" data-testid={`area-row-${area.id}`}>
                          <td className="py-3 px-4 text-[#2D2420] font-medium">{area.name}</td>
                          <td className="py-3 px-4">
                            <Badge className={area.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {area.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditArea(area)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                data-testid={`edit-area-${area.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteArea(area)}
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-area-${area.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Dialog open={showAreaModal} onOpenChange={setShowAreaModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#2D2420]">{editingArea ? 'Edit Area' : 'Add Area'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Name</Label>
                    <Input
                      value={areaForm.name}
                      onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })}
                      placeholder="Enter area name"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      data-testid="area-name-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-[#5C4033]">Active</Label>
                      <p className="text-sm text-[#A8A8A8]">Enable this area</p>
                    </div>
                    <Switch
                      checked={areaForm.is_active}
                      onCheckedChange={(checked) => setAreaForm({ ...areaForm, is_active: checked })}
                      data-testid="area-active"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveArea}
                    disabled={areaSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                    data-testid="save-area-btn"
                  >
                    {areaSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {canManageSettings && (
          <TabsContent value="short-urls" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">Short URLs</CardTitle>
                  <CardDescription>Create short links with tracking for analysis</CardDescription>
                </div>
                <Button
                  onClick={() => { resetShortUrlForm(); setShowShortUrlModal(true); }}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Short URL
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {shortUrlLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                  </div>
                ) : shortUrls.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-[#A8A8A8] gap-2">
                    <span>No short URLs yet</span>
                    <Button
                      variant="outline"
                      onClick={createSampleShortUrls}
                      disabled={creatingShortSamples}
                      className="border-[#E5E5E5]"
                    >
                      {creatingShortSamples ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create Sample Links
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Original URL</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Short URL</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Clicks</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5E5]">
                        {shortUrls.map((item) => (
                          <tr key={item.id} className="hover:bg-[#FAFAF9]/50">
                            <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name || '-'}</td>
                            <td className="py-3 px-4 text-[#2D2420] max-w-xs truncate" title={item.original_url}>
                              {item.original_url}
                            </td>
                            <td className="py-3 px-4 text-[#2D2420]">
                              <a
                                href={`${shortUrlBase}${item.code}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#C97A5E] hover:underline"
                              >
                                {shortUrlBase}{item.code}
                              </a>
                            </td>
                            <td className="py-3 px-4 text-right text-[#2D2420]">{item.total_clicks || 0}</td>
                            <td className="py-3 px-4">
                              <Badge className={item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {item.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCopyShortUrl(item.code)}
                                  className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                  title="Copy"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={showShortUrlModal} onOpenChange={setShowShortUrlModal}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-[#2D2420]">Create Short URL</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Name</Label>
                    <Input
                      value={shortUrlForm.name}
                      onChange={(e) => setShortUrlForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Optional label"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Original URL *</Label>
                    <Input
                      value={shortUrlForm.original_url}
                      onChange={(e) => setShortUrlForm((prev) => ({ ...prev, original_url: e.target.value }))}
                      placeholder="https://example.com"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Custom Code</Label>
                    <Input
                      value={shortUrlForm.code}
                      onChange={(e) => setShortUrlForm((prev) => ({ ...prev, code: e.target.value }))}
                      placeholder="Optional short code"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Source</Label>
                      <Input
                        value={shortUrlForm.utm_source}
                        onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_source: e.target.value }))}
                        placeholder="utm_source"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Medium</Label>
                      <Input
                        value={shortUrlForm.utm_medium}
                        onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_medium: e.target.value }))}
                        placeholder="utm_medium"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Campaign</Label>
                      <Input
                        value={shortUrlForm.utm_campaign}
                        onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_campaign: e.target.value }))}
                        placeholder="utm_campaign"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Content</Label>
                      <Input
                        value={shortUrlForm.utm_content}
                        onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_content: e.target.value }))}
                        placeholder="utm_content"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Term</Label>
                      <Input
                        value={shortUrlForm.utm_term}
                        onChange={(e) => setShortUrlForm((prev) => ({ ...prev, utm_term: e.target.value }))}
                        placeholder="utm_term"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-[#5C4033]">Active</Label>
                        <p className="text-sm text-[#A8A8A8]">Enable short URL</p>
                      </div>
                      <Switch
                        checked={shortUrlForm.is_active}
                        onCheckedChange={(checked) => setShortUrlForm((prev) => ({ ...prev, is_active: checked }))}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveShortUrl}
                    disabled={shortUrlSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {shortUrlSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {canManageSettings && (
          <TabsContent value="qr-codes" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">QR Codes</CardTitle>
                  <CardDescription>Generate QR codes tied to short URLs for scan analytics</CardDescription>
                </div>
                <Button
                  onClick={() => { resetQrCodeForm(); setShowQrCodeModal(true); }}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create QR Code
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {qrCodeLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                  </div>
                ) : qrCodes.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
                    No QR codes yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">QR</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Original URL</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Short URL</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Scans</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5E5]">
                        {qrCodes.map((item) => {
                          const qrLink = `${shortUrlBase}${item.code}?qr=${item.id}`;
                          return (
                            <tr key={item.id} className="hover:bg-[#FAFAF9]/50">
                              <td className="py-3 px-4">
                                <QRCodeCanvas
                                  id={`qr-canvas-${item.id}`}
                                  value={qrLink}
                                  size={256}
                                  style={{ width: 48, height: 48 }}
                                />
                              </td>
                              <td className="py-3 px-4 text-[#2D2420] font-medium">{item.name || '-'}</td>
                              <td className="py-3 px-4 text-[#2D2420] max-w-xs truncate" title={item.original_url}>
                                {item.original_url}
                              </td>
                              <td className="py-3 px-4 text-[#2D2420]">
                                <a
                                  href={qrLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#C97A5E] hover:underline"
                                >
                                  {qrLink}
                                </a>
                              </td>
                              <td className="py-3 px-4 text-right text-[#2D2420]">{item.total_scans || 0}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleCopyQrLink(item.code, item.id)}
                                    className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                    title="Copy"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDownloadQr(item.id, item.name)}
                                    className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                    title="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={showQrCodeModal} onOpenChange={setShowQrCodeModal}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-[#2D2420]">Create QR Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Name</Label>
                    <Input
                      value={qrCodeForm.name}
                      onChange={(e) => setQrCodeForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Optional label"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Original URL *</Label>
                    <Input
                      value={qrCodeForm.original_url}
                      onChange={(e) => setQrCodeForm((prev) => ({ ...prev, original_url: e.target.value }))}
                      placeholder="https://example.com"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#5C4033]">Custom Code</Label>
                    <Input
                      value={qrCodeForm.code}
                      onChange={(e) => setQrCodeForm((prev) => ({ ...prev, code: e.target.value }))}
                      placeholder="Optional short code"
                      className="border-[#E5E5E5] focus:border-[#C97A5E]"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Source</Label>
                      <Input
                        value={qrCodeForm.utm_source}
                        onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_source: e.target.value }))}
                        placeholder="utm_source"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Medium</Label>
                      <Input
                        value={qrCodeForm.utm_medium}
                        onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_medium: e.target.value }))}
                        placeholder="utm_medium"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Campaign</Label>
                      <Input
                        value={qrCodeForm.utm_campaign}
                        onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_campaign: e.target.value }))}
                        placeholder="utm_campaign"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Content</Label>
                      <Input
                        value={qrCodeForm.utm_content}
                        onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_content: e.target.value }))}
                        placeholder="utm_content"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#5C4033]">UTM Term</Label>
                      <Input
                        value={qrCodeForm.utm_term}
                        onChange={(e) => setQrCodeForm((prev) => ({ ...prev, utm_term: e.target.value }))}
                        placeholder="utm_term"
                        className="border-[#E5E5E5] focus:border-[#C97A5E]"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-[#5C4033]">Active</Label>
                        <p className="text-sm text-[#A8A8A8]">Enable QR code</p>
                      </div>
                      <Switch
                        checked={qrCodeForm.is_active}
                        onCheckedChange={(checked) => setQrCodeForm((prev) => ({ ...prev, is_active: checked }))}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={handleSaveQrCode}
                    disabled={qrCodeSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {qrCodeSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {canManageSettings && (
          <TabsContent value="testimonials" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420]">Testimonials</CardTitle>
                <CardDescription>Review customer testimonials and mark featured testimonials for website display.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {testimonialsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                  </div>
                ) : testimonials.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
                    No testimonials yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Review</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Rating</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Approved</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Featured</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5E5]">
                        {testimonials.map((item) => (
                          <tr key={item.id} className="hover:bg-[#FAFAF9]/50">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full overflow-hidden bg-[#F2ECE8] flex items-center justify-center text-[#5C4033] text-xs font-semibold">
                                  {item.photo_url ? (
                                    <img
                                      src={getAssetUrl(item.photo_url)}
                                      alt={item.name || 'Customer'}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    (item.name || '?').slice(0, 1).toUpperCase()
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-[#2D2420]">{item.name || '-'}</p>
                                  <p className="text-xs text-[#A8A8A8]">{item.email || '-'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-[#2D2420] max-w-md">
                              <p className="line-clamp-2">{item.testimonial || '-'}</p>
                            </td>
                            <td className="py-3 px-4 text-center text-[#2D2420] font-medium">{item.rating || 0}/5</td>
                            <td className="py-3 px-4 text-center">
                              <Switch
                                checked={item.is_approved}
                                onCheckedChange={(checked) => updateTestimonialFlag(item.id, { is_approved: checked })}
                                disabled={testimonialUpdatingId === item.id}
                              />
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Switch
                                checked={item.is_featured}
                                onCheckedChange={(checked) => updateTestimonialFlag(item.id, { is_featured: checked })}
                                disabled={testimonialUpdatingId === item.id}
                              />
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Switch
                                checked={item.is_active}
                                onCheckedChange={(checked) => updateTestimonialFlag(item.id, { is_active: checked })}
                                disabled={testimonialUpdatingId === item.id}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canManageSettings && (
          <TabsContent value="users" className="space-y-6 mt-6">
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#C97A5E]" /> My MFA Security
                </CardTitle>
                <CardDescription>Protect your admin login with an authenticator app.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Badge className={mfaStatus.enabled ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                    {mfaStatus.enabled ? 'MFA Enabled' : 'MFA Disabled'}
                  </Badge>
                  {mfaStatus.required ? (
                    <Badge className="bg-[#C97A5E]/10 text-[#C97A5E]">Required for admin logins</Badge>
                  ) : null}
                </div>

                {!mfaStatus.enabled ? (
                  <div className="space-y-4">
                    <Button onClick={handleSetupMfa} disabled={mfaBusy} className="bg-[#C97A5E] hover:bg-[#B06850]">
                      {mfaBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                      Setup MFA
                    </Button>

                    {mfaSetupSecret ? (
                      <div className="grid gap-4 md:grid-cols-[220px_1fr] items-start rounded-xl border border-[#E5E5E5] p-4">
                        <div className="flex justify-center">
                          <QRCodeCanvas value={mfaSetupUrl} size={180} includeMargin />
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[#5C4033]">Secret</Label>
                            <Input value={mfaSetupSecret} readOnly />
                          </div>
                          <div>
                            <Label className="text-[#5C4033]">Verify Code</Label>
                            <Input
                              value={mfaVerifyCode}
                              onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="Enter 6-digit code from your authenticator app"
                            />
                          </div>
                          <Button onClick={handleVerifyMfa} disabled={mfaBusy} className="bg-[#2D2420] hover:bg-[#1e1714]">
                            Verify and Enable
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label className="text-[#5C4033]">Current Password</Label>
                      <Input
                        type="password"
                        value={mfaDisablePassword}
                        onChange={(e) => setMfaDisablePassword(e.target.value)}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <Label className="text-[#5C4033]">Authenticator Code</Label>
                      <Input
                        value={mfaDisableCode}
                        onChange={(e) => setMfaDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Button variant="outline" onClick={handleDisableMfa} disabled={mfaBusy}>
                        Disable MFA
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-[#2D2420]">Admin Users</CardTitle>
                  <CardDescription>Manage admin panel users and their roles</CardDescription>
                </div>
                <Button
                  onClick={() => { resetUserForm(); setShowUserModal(true); }}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                  data-testid="add-admin-user-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add User
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">User</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Role</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Permissions</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {adminUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-[#FAFAF9]/50" data-testid={`admin-user-row-${user.id}`}>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-[#2D2420]">{user.name}</p>
                              <p className="text-sm text-[#A8A8A8]">@{user.username}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={cn("capitalize", ROLE_COLORS[user.role] || ROLE_COLORS.accountant)}>
                              {user.role}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {user.permissions?.includes('all') ? (
                                <Badge className="bg-[#C97A5E]/10 text-[#C97A5E] text-xs">Full Access</Badge>
                              ) : (
                                user.permissions?.slice(0, 3).map((perm) => (
                                  <Badge key={perm} className="bg-gray-100 text-gray-600 text-xs">{perm}</Badge>
                                ))
                              )}
                              {user.permissions?.length > 3 && !user.permissions?.includes('all') && (
                                <Badge className="bg-gray-100 text-gray-600 text-xs">+{user.permissions.length - 3}</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={user.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditUser(user)}
                                className="text-[#5C4033] hover:bg-[#FAFAF9]"
                                data-testid={`edit-admin-${user.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {user.id !== admin?.id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setSelectedUser(user); setShowDeleteDialog(true); }}
                                  className="text-red-600 hover:bg-red-50"
                                  data-testid={`delete-admin-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Role Permissions Info */}
            <Card className="bg-white border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#C97A5E]" /> Role Permissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-[#FAFAF9] rounded-xl">
                    <Badge className="bg-[#C97A5E]/10 text-[#C97A5E] mb-2">Admin</Badge>
                    <p className="text-sm text-[#2D2420]">Full access to all features including user management and settings.</p>
                  </div>
                  <div className="p-4 bg-[#FAFAF9] rounded-xl">
                    <Badge className="bg-blue-100 text-blue-800 mb-2">Manager</Badge>
                    <p className="text-sm text-[#2D2420]">Access to dashboard, staff, customers, products, orders, payments, leads, and reports.</p>
                  </div>
                  <div className="p-4 bg-[#FAFAF9] rounded-xl">
                    <Badge className="bg-green-100 text-green-800 mb-2">Accountant</Badge>
                    <p className="text-sm text-[#2D2420]">Access to dashboard, orders, payments, and reports only.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Payment Methods Tab */}
        <TabsContent value="payments" className="space-y-6 mt-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg text-[#2D2420]">Payment Methods</CardTitle>
              <CardDescription>Configure available payment methods for collections</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['cash', 'upi', 'bank', 'cheque', 'card', 'credit'].map((method) => (
                  <div
                    key={method}
                    onClick={() => canManageSettings && togglePaymentMethod(method)}
                    className={cn(
                      "p-4 rounded-xl border-2 cursor-pointer transition-all",
                      settings.payment_methods?.includes(method)
                        ? "border-[#C97A5E] bg-[#C97A5E]/5"
                        : "border-[#E5E5E5] bg-white hover:border-[#D2B48C]",
                      !canManageSettings && "cursor-not-allowed opacity-60"
                    )}
                    data-testid={`payment-method-${method}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[#2D2420] capitalize">{method}</span>
                      <Switch
                        checked={settings.payment_methods?.includes(method)}
                        onCheckedChange={() => canManageSettings && togglePaymentMethod(method)}
                        disabled={!canManageSettings}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg text-[#2D2420]">BOS UPI QR</CardTitle>
              <CardDescription>
                Configure the business UPI QR that will appear on Bill of Supply PDFs below the signature text.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between rounded-xl border border-[#E5E5E5] p-4">
                <div className="space-y-1">
                  <Label className="text-[#5C4033]">Enable UPI QR on BOS</Label>
                  <p className="text-sm text-[#6B5B50]">
                    When enabled, customers can scan the printed BOS and pay via any UPI app.
                  </p>
                </div>
                <Switch
                  checked={Boolean(settings.upi_enabled)}
                  onCheckedChange={(checked) => canManageSettings && setSettings({ ...settings, upi_enabled: checked })}
                  disabled={!canManageSettings}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">UPI ID</Label>
                  <Input
                    value={settings.upi_id || ''}
                    onChange={(e) => setSettings({ ...settings, upi_id: e.target.value })}
                    placeholder="payments@bank"
                    disabled={!canManageSettings}
                    data-testid="settings-upi-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">UPI Payee Name</Label>
                  <Input
                    value={settings.upi_payee_name || ''}
                    onChange={(e) => setSettings({ ...settings, upi_payee_name: e.target.value })}
                    placeholder="Agniya Spices"
                    disabled={!canManageSettings}
                    data-testid="settings-upi-payee-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div className="space-y-2">
                  <Label className="text-[#5C4033]">UPI Note Prefix</Label>
                  <Input
                    value={settings.upi_note_prefix || ''}
                    onChange={(e) => setSettings({ ...settings, upi_note_prefix: e.target.value })}
                    placeholder="AGNIYA"
                    disabled={!canManageSettings}
                    data-testid="settings-upi-note-prefix"
                  />
                  <p className="text-xs text-[#8A776A]">
                    Example BOS reference note: <span className="font-medium">{buildBosUpiNote(settings, 'ASAC00001') || 'ASAC00001'}</span>
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-[#E5E5E5] p-4">
                  <div className="space-y-1 pr-4">
                    <Label className="text-[#5C4033]">Show UPI text on BOS</Label>
                    <p className="text-sm text-[#6B5B50]">
                      Prints the UPI ID under the QR without changing the locked BOS layout.
                    </p>
                  </div>
                  <Switch
                    checked={Boolean(settings.show_upi_text_on_bos)}
                    onCheckedChange={(checked) => canManageSettings && setSettings({ ...settings, show_upi_text_on_bos: checked })}
                    disabled={!canManageSettings}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-dashed border-[#D8C0B4] bg-[#FCF8F5] p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2 max-w-xl">
                    <h4 className="font-semibold text-[#2D2420]">Live QR Preview</h4>
                    <p className="text-sm text-[#6B5B50]">
                      This preview uses the same UPI payload logic as BOS generation. The QR note defaults to the BOS number and
                      adds your prefix when provided.
                    </p>
                    <div className="space-y-1 text-sm text-[#5C4033]">
                      <p><span className="font-medium">Sample BOS Ref:</span> ASAC00001</p>
                      <p><span className="font-medium">Sample Note:</span> {buildBosUpiNote(settings, 'ASAC00001') || 'ASAC00001'}</p>
                      <p className="break-all"><span className="font-medium">UPI URI:</span> {buildBosUpiUri(settings, 'ASAC00001') || 'Preview will appear after entering valid UPI details'}</p>
                    </div>
                  </div>
                  <div className="flex min-w-[220px] justify-center">
                    <div ref={bosUpiQrRef} className="rounded-2xl bg-white p-4 shadow-sm border border-[#E5E5E5]">
                      {buildBosUpiUri(settings, 'ASAC00001') ? (
                        <div className="space-y-3 text-center">
                          <p className="text-sm font-semibold text-[#2D2420]">Scan to Pay</p>
                          <QRCodeCanvas
                            value={buildBosUpiUri(settings, 'ASAC00001')}
                            size={180}
                            includeMargin
                            level="M"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={handleDownloadBosUpiQr}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download QR
                          </Button>
                        </div>
                      ) : (
                        <div className="flex h-[180px] w-[180px] items-center justify-center rounded-xl border border-dashed border-[#D8C0B4] text-center text-sm text-[#8A776A] px-4">
                          Enable UPI QR and enter a valid UPI ID and payee name to preview the BOS payment QR.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {canManageSettings && (
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
                data-testid="save-payment-methods-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Payment Settings
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit User Modal */}
      <Dialog open={showUserModal} onOpenChange={(open) => { if (!open) resetUserForm(); setShowUserModal(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              {isEditing ? 'Edit Admin User' : 'Add Admin User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!isEditing && (
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Username *</Label>
                <Input
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  placeholder="Enter username"
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  data-testid="admin-username-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#5C4033]">{isEditing ? 'New Password (optional)' : 'Password *'}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                placeholder={isEditing ? 'Leave blank to keep current' : 'Enter password'}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="admin-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="Enter name"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Role</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(v) => setUserForm({ ...userForm, role: v })}
              >
                <SelectTrigger className="border-[#E5E5E5]" data-testid="admin-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (Full Access)</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { resetUserForm(); setShowUserModal(false); }}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={isEditing ? handleUpdateUser : handleAddUser}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
              data-testid="save-admin-user-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Admin User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedUser?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-admin-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
